import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from './cn'

/**
 * Toasts: the outcome of an action, said once and then out of the way.
 *
 * Every page used to keep its own `notice` (or `message`) state and render it as a green paragraph
 * at the top of the page: fifteen copies, each slightly different, some announced to a screen
 * reader and most not, none of them ever going away until the page was left. The design tokens have
 * had a `z-toast` layer all along with nothing on it.
 *
 * **What goes here and what does not.** A toast reports what an action did ("Reboot command
 * sent", "The job could not be started"). A failure that belongs to a form, such as a field the
 * relay refused or a dialog's save that failed, stays inline next to the form, because that is where
 * the operator is looking and what they need to fix.
 *
 * Accessibility: success and information are `role="status"` (announced politely), errors
 * `role="alert"` (announced at once). Success and information dismiss themselves after
 * {@link TOAST_TIMEOUT_MS}, paused while the pointer or focus is on them; errors stay until
 * dismissed, since a message that vanishes before it is read is no message at all. Every toast has a
 * dismiss button.
 */

export type ToastTone = 'success' | 'error' | 'info'

export interface ToastApi {
  /** Shows a toast and returns its id. */
  show: (message: ReactNode, tone?: ToastTone) => number
  success: (message: ReactNode) => number
  error: (message: ReactNode) => number
  info: (message: ReactNode) => number
  dismiss: (id: number) => void
}

interface ToastItem {
  id: number
  message: ReactNode
  tone: ToastTone
}

/** How long a success or information toast stays. Errors stay until dismissed. */
export const TOAST_TIMEOUT_MS = 6_000

/** At most this many on screen; the oldest goes first. */
const MAX_TOASTS = 4

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const show = useCallback((message: ReactNode, tone: ToastTone = 'info') => {
    const id = nextId.current++
    setItems((current) => {
      // The same words twice (a button pressed again) replace the first rather than stacking up.
      const others = current.filter((item) => !(item.tone === tone && item.message === message))
      return [...others, { id, message, tone }].slice(-MAX_TOASTS)
    })
    return id
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (message) => show(message, 'success'),
      error: (message) => show(message, 'error'),
      info: (message) => show(message, 'info'),
    }),
    [show, dismiss],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        Portalled to <body>, so the toasts sit above every page's own stacking and a component
        rendered on its own leaves its container exactly as it would without them.
      */}
      {createPortal(
        <div
          data-toast-viewport=""
          className="pointer-events-none fixed bottom-md right-md z-toast flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-sm"
        >
          {items.map((item) => (
            <Toast key={item.id} item={item} onDismiss={dismiss} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

const TONE: Record<ToastTone, string> = {
  success: 'border-success bg-success-bg text-success',
  error: 'border-danger bg-danger-bg text-danger',
  info: 'border-line bg-bg-secondary text-fg',
}

function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false)
  const lasting = item.tone === 'error'

  useEffect(() => {
    if (lasting || paused) return
    const timer = setTimeout(() => onDismiss(item.id), TOAST_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [item.id, lasting, paused, onDismiss])

  return (
    <div
      // Each toast is its own live region: a `status` is announced politely and an `alert` at once.
      // A role on the item rather than on an always-present container, so a page has no extra
      // empty alert in it while nothing has happened.
      role={item.tone === 'error' ? 'alert' : 'status'}
      data-toast={item.tone}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={cn(
        'pointer-events-auto flex items-start gap-sm rounded-md border px-md py-sm text-sm shadow-lg',
        TONE[item.tone],
      )}
    >
      <div className="min-w-0 flex-1 break-words">{item.message}</div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(item.id)}
        className="shrink-0 rounded-md px-xs leading-none opacity-80 hover:opacity-100"
      >
        ×
      </button>
    </div>
  )
}

/**
 * The toast service.
 *
 * Outside a provider (a component rendered on its own in a test) it falls back to a service that
 * shows nothing rather than throwing, so a toast is never the reason a page fails to render. The
 * application, the federated module and the test harness all provide one.
 */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? DETACHED
}

const DETACHED: ToastApi = {
  show: () => 0,
  success: () => 0,
  error: () => 0,
  info: () => 0,
  dismiss: () => undefined,
}
